'use client';

import { useRouter } from '@bprogress/next/app';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { VariantProps } from 'tailwind-variants';

import { UiAuthInputCode } from '@/fo/ui/auth/input/code';
import { UiKitButtonBack } from '@/fo/ui/kit/button/back';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitInputBase } from '@/fo/ui/kit/input/base';
import { UiKitInputPassword } from '@/fo/ui/kit/input/password';
import { FormMessage, useForm } from '@/fo/ui/mod/form/base';
import { utilEventAuthModal } from '@/fo/util/event-auth-modal';
import { phoneMask } from '@/fo/util/format-phone-number';

import { registerByPhone, sendAuthCode } from '../api/registration';
import { Agreements } from '../components/Agreements/Agreements';

import { styles } from './FeatureAuthFormRegistration.styles';
import { Data, formAction } from './formAction';

type Variants = VariantProps<typeof styles>;
type NativeElement = React.HTMLAttributes<HTMLDivElement>;

export interface FeatureAuthFormRegistrationProps {
  children?: React.ReactNode;
  color?: Variants['color'];
  onClick?: NativeElement['onClick'];
  agreementPrivacy?: string;
}

export function FeatureAuthFormRegistration({
  color,
  agreementPrivacy,
  ...props
}: FeatureAuthFormRegistrationProps) {
  const slots = styles({ color });
  const t = useTranslations();
  const router = useRouter();
  const { action, getFieldProps, pending, state } = useForm(
    formAction,
    {},
    {
      displayMessageInToast: false
    }
  );
  const [formStep, setFormStep] = useState<'registration' | 'authCode'>(
    'registration'
  );
  const [messageHidden, setMessageHidden] = useState(false);

  const formData = state.data as Data;

  useEffect(() => {
    if (!pending && state?.message) setMessageHidden(false);
  }, [pending, state?.message]);

  useEffect(() => {
    if (state.status === 'success' && !pending) {
      setFormStep('authCode');
    }
  }, [state.status, pending]);

  return (
    <div
      className={slots.base()}
      {...props}
    >
      <h2 className={slots.title()}>{t('auth.titleRegistration')}</h2>
      {formStep === 'registration' && (
        <form action={action}>
          <div className={slots.fields()}>
            <UiKitInputBase
              required
              label={t('form.label.name')}
              {...getFieldProps('name')}
              disabled={pending}
            />
            <UiKitInputBase
              required
              label={t('form.label.lastName')}
              {...getFieldProps('lastname')}
              disabled={pending}
            />
            <UiKitInputBase
              required
              label={t('form.label.email')}
              {...getFieldProps('email')}
              disabled={pending}
            />
            <UiKitInputBase
              required
              label={t('form.label.phone')}
              {...getFieldProps('phone')}
              disabled={pending}
              mask={phoneMask}
            />
            <UiKitInputPassword
              required
              label={t('form.label.password')}
              {...getFieldProps('password')}
              disabled={pending}
              helperText={getFieldProps('password').helperText}
            />
            <UiKitInputPassword
              required
              label={t('form.label.passwordConfirm')}
              {...getFieldProps('passwordConfirm')}
              disabled={pending}
            />
          </div>

          <Agreements
            getFieldProps={getFieldProps}
            pending={pending}
            agreementPrivacy={agreementPrivacy}
          />

          <UiKitButtonBase
            type="submit"
            className={slots.button()}
            loading={pending}
            color="secondary"
          >
            <span>{t('form.button.registration')}</span>
          </UiKitButtonBase>

          <UiKitButtonBase
            type="button"
            color="transparent"
            className={slots.alreadyHaveAccount()}
            onClick={() => utilEventAuthModal.switchToLoginTab()}
          >
            <span className="heading-s">{t('auth.alreadyHaveAccount')}</span>
          </UiKitButtonBase>

          {!pending && state?.message && state?.status && !messageHidden && (
            <FormMessage
              message={state.message}
              status={state.status}
              duration={3000}
              onDismiss={() => setMessageHidden(true)}
            />
          )}
        </form>
      )}
      {formStep === 'authCode' && (
        <>
          <div>
            <UiKitButtonBack
              className={slots.backButton()}
              onClick={() => setFormStep('registration')}
            />
          </div>
          <UiAuthInputCode
            phoneNumber={(state.data?.phone as string) ?? ''}
            onSendCode={async (code) => {
              const response = await registerByPhone(
                (state.data?.phone as string) ?? '',
                code,
                formData
              );

              if (response.status === 'success') {
                utilEventAuthModal.close();
                setFormStep('registration');
                router.push('/account');
              }

              return response;
            }}
            onResend={() => sendAuthCode((state.data?.phone as string) ?? '')}
          />
        </>
      )}
    </div>
  );
}
